import { createTheme } from '@mui/material'
import { ThemeMode } from './ThemeContext';

declare module '@mui/material/styles' {
    interface Palette {
        spectre: {
            page: string;
            surface: string;
            surfaceMuted: string;
            input: string;
            border: string;
            textPrimary: string;
            textSecondary: string;
            textMuted: string;
            navigation: string;
            navigationHover: string;
            inactiveCard: string;
            refreshButtonSurface: string;
            refreshButtonSurfaceHover: string;
            refreshButtonBorder: string;
            refreshButtonBorderHover: string;
            refreshButtonText: string;
            warningSurface: string;
            warningSurfaceHover: string;
            warningBorder: string;
            warningAccent: string;
            warningInputSurface: string;
            fileCardSurface: string;
            fileCardSurfaceHover: string;
            fileAccentXml: string;
            fileAccentPcd: string;
            fileAccentImage: string;
            fileAccentDefault: string;
        };
    }

    interface PaletteOptions {
        spectre?: Partial<Palette['spectre']>;
    }
}

const themeColors = {
    light: {
        page: '#F5F5F8',
        surface: '#FFFFFF',
        surfaceMuted: '#E2E2E9',
        input: '#EAEAF0',
        border: '#D3D3DE',
        textPrimary: '#262D6D',
        textSecondary: '#4B4E65',
        textMuted: '#636383',
        settingsTextPrimary: 'rgba(0, 0, 0, 0.87)',
        settingsTextSecondary: 'rgba(0, 0, 0, 0.54)',
        customFieldLabel: '#666666',
        navigation: '#00004B',
        navigationHover: '#384085',
        inactiveCard: '#E2E2E9',
        refreshButtonSurface: '#00004B',
        refreshButtonSurfaceHover: '#000020',
        refreshButtonBorder: '#00004B',
        refreshButtonBorderHover: '#000075',
        refreshButtonText: '#FFFFFF',
        navbarButton: '#2a3550',
        warningSurface: '#FFF9E6',
        warningSurfaceHover: 'rgba(255, 204, 51, 0.12)',
        warningBorder: '#FFCC33',
        warningAccent: '#F1C21B',
        warningInputSurface: '#F1C21B',
        fileCardSurface: '#FFFFFF',
        fileCardSurfaceHover: '#FFFFFF',
        fileAccentXml: '#198754',
        fileAccentPcd: '#087f8c',
        fileAccentImage: '#7c3aed',
        fileAccentDefault: '#d66a18'
    },
    dark: {
        page: '#090D14',
        surface: '#0F1623',
        surfaceMuted: '#151F32',
        input: '#131B2A',
        border: '#24324D',
        textPrimary: '#F8FAFC',
        textSecondary: '#CBD5E1',
        textMuted: '#94A3B8',
        settingsTextPrimary: '#F8FAFC',
        settingsTextSecondary: '#CBD5E1',
        customFieldLabel: '#A6A6A6',
        navigation: '#0F172A',
        navigationHover: '#1E293B',
        inactiveCard: '#121B2C',
        refreshButtonSurface: '#243B64',
        refreshButtonSurfaceHover: '#304C7A',
        refreshButtonBorder: '#8191AE',
        refreshButtonBorderHover: '#B6C2D6',
        refreshButtonText: '#FFFFFF',
        navbarButton: '#2a3550',
        warningSurface: '#3D3109',
        warningSurfaceHover: '#4A3B08',
        warningBorder: '#9A7200',
        warningAccent: '#F1C21B',
        warningInputSurface: '#8A7722',
        fileCardSurface: '#121B2C',
        fileCardSurfaceHover: '#151F32',
        fileAccentXml: '#198754',
        fileAccentPcd: '#087f8c',
        fileAccentImage: '#7c3aed',
        fileAccentDefault: '#d66a18'
    },
};

export const createSpectreTheme = (mode: ThemeMode) => {
    const colors = themeColors[mode];
    const theme = createTheme({
        palette: {
            mode,
            background: {
                default: colors.page,
                paper: colors.surface,
            },
            text: {
                primary: colors.textPrimary,
                secondary: colors.textSecondary,
                disabled: colors.textMuted,
            },
            primary: {
                light: '#000075',
                main: mode === 'light' ? '#00004B' : '#f8f8fc',
                dark: '#000020',
            },
            secondary: {
                light: '#F8B160',
                main: '#F7914B',
                dark: '#F48430',
            },
            error: {
                main: '#D43636',
            },
            warning: {
                light: mode === 'light' ? '#FFF9E6' : '#403719',
                main: '#FFCC33',
                dark: mode === 'light' ? '#996600' : '#FFF4D1',
                contrastText: mode === 'light' ? '#805500' : '#FFF4D1',
            },
            spectre: colors,
        },
        typography: {
            button: {
                textTransform: 'none',
            },
            fontFamily: ['Open Sans', 'sans-serif'].join(','),
        },
    });

    return createTheme(theme, {
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    ':root': {
                        '--spectre-page': colors.page,
                        '--spectre-surface': colors.surface,
                        '--spectre-surface-muted': colors.surfaceMuted,
                        '--spectre-input': colors.input,
                        '--spectre-border': colors.border,
                        '--spectre-text-primary': colors.textPrimary,
                        '--spectre-text-secondary': colors.textSecondary,
                        '--spectre-text-muted': colors.textMuted,
                        '--spectre-passive-scan-dot': mode === 'dark' ? colors.textPrimary : colors.surface,
                        '--spectre-settings-text-primary': colors.settingsTextPrimary,
                        '--spectre-settings-text-secondary': colors.settingsTextSecondary,
                        '--spectre-custom-field-label': colors.customFieldLabel,
                        '--spectre-navigation': colors.navigation,
                        '--spectre-navigation-hover': colors.navigationHover,
                        '--spectre-inactive-card': colors.inactiveCard,
                        '--spectre-warning-surface': colors.warningSurface,
                        '--spectre-warning-surface-hover': colors.warningSurfaceHover,
                        '--spectre-warning-border': colors.warningBorder,
                        '--spectre-warning-accent': colors.warningAccent,
                        '--spectre-warning-input-surface': colors.warningInputSurface,
                        '--spectre-file-card-surface': colors.fileCardSurface,
                        '--spectre-file-card-surface-hover': colors.fileCardSurfaceHover,
                    },
                    body: {
                        backgroundColor: colors.page,
                        color: colors.textPrimary,
                    },
                    '*::-webkit-scrollbar': {
                        width: 6,
                        height: 6,
                    },
                    '*::-webkit-scrollbar-track': {
                        backgroundColor: mode === 'dark' ? '#0B111C' : colors.surfaceMuted,
                    },
                    '*::-webkit-scrollbar-thumb': {
                        backgroundColor: colors.border,
                    },
                    '*::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: mode === 'dark' ? '#324468' : colors.textMuted,
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.navigation,
                        borderBottom: `1px solid ${colors.border}`,
                        backgroundImage: 'none',
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        backgroundColor: colors.surface,
                        border: `1px solid ${colors.border}`,
                        backgroundImage: 'none',
                        boxShadow: mode === 'dark'
                            ? '0 8px 24px rgba(148, 163, 184, 0.22), 0 2px 8px rgba(0, 0, 0, 0.55)'
                            : undefined,
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.input,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.border,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: mode === 'dark' ? '#324468' : colors.textMuted,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: mode === 'dark' ? '#06B6D4' : undefined,
                        },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        color: mode === 'dark' ? '#FFFFFF' : undefined,
                        '&.Mui-checked': {
                            color: mode === 'dark' ? '#FFFFFF' : undefined,
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        '&.Passive-Scan-Button': {
                            color: '#FFFFFF',
                            height: '65px',
                            fontWeight: 700,
                            fontSize: 20,
                            '&:hover': {
                                boxShadow: `inset 0 0 0 1px ${colors.border}`,
                            },
                            '&:disabled': {
                                boxShadow: `inset 0 0 0 1px ${colors.border}`,
                                color: '#FFFFFF',
                            },
                        },
                        '&.Scan-Button': {
                            color: '#FFFFFF',
                            backgroundColor: theme.palette.secondary.main,
                            height: '65px',
                            fontWeight: 700,
                            fontSize: 20,
                            '&:hover': {
                                backgroundColor: theme.palette.secondary.dark,
                            },
                            '&:disabled': {
                                backgroundColor: colors.surfaceMuted,
                                boxShadow: `inset 0 0 0 1px ${colors.border}`,
                                color: colors.textMuted,
                            },
                            '&.Scan-Button-Active:disabled': {
                                backgroundColor: theme.palette.secondary.main,
                                color: '#FFFFFF',
                                opacity: 0.7,
                            },
                        },
                        '&.Orange-Button, &.Sign-In-Button': {
                            color: '#FFFFFF',
                            backgroundColor: theme.palette.secondary.dark,
                            '&:hover': {
                                backgroundColor: theme.palette.secondary.main,
                            },
                            '&:disabled': {
                                backgroundColor: colors.surfaceMuted,
                                color: colors.textMuted,
                            },
                        },
                        '&.Sign-In-Button': {
                            height: '65px',
                            fontWeight: 700,
                            fontSize: 20,
                        },
                        '&.Blue-Button, &.Upload-Export-Blue, &.Upload-Export-Blue-Small': {
                            color: '#FFFFFF',
                            backgroundColor: mode === 'dark' ? '#101A38' : theme.palette.primary.main,
                            boxShadow: mode === 'dark' ? 'inset 0 0 0 1px #8593af' : undefined,
                            '&:hover': {
                                backgroundColor: mode === 'dark' ? '#172554' : theme.palette.primary.light,
                                boxShadow: mode === 'dark' ? 'inset 0 0 0 1px #3B82F6' : `inset 0 0 0 1px ${colors.border}`,
                            },
                            '&:disabled, &.Mui-disabled': {
                                color: mode === 'light' ? '#FFFFFF' : colors.textMuted,
                                backgroundColor: mode === 'light' ? colors.border : colors.inactiveCard,
                                boxShadow: `inset 0 0 0 1px ${colors.border}`,
                                opacity: mode === 'dark' ? 0.65 : undefined,
                            },
                        },
                        '&.Modal-Close-Button': {
                            backgroundColor: mode === 'dark' ? colors.surfaceMuted : theme.palette.primary.main,
                            boxShadow: `inset 0 0 0 1px ${mode === 'dark' ? colors.textPrimary : theme.palette.primary.main}`,
                            '&:hover': {
                                backgroundColor: mode === 'dark' ? colors.surface : theme.palette.primary.light,
                                boxShadow: `inset 0 0 0 1px ${mode === 'dark' ? colors.textPrimary : colors.border}`,
                            },
                        },
                        '&.Upload-Export-Blue': {
                            height: '65px',
                            fontWeight: 700,
                            fontSize: 20,
                        },
                        '&.Upload-Export-Blue-Small': {
                            height: '32px',
                            minWidth: '80px',
                            fontWeight: 600,
                            fontSize: 15,
                            padding: '0 16px',
                            lineHeight: 1.2,
                            boxShadow: 'none',
                        },
                        '&.Nav-Bar-Button': {
                            backgroundColor: mode === 'light' ? '#2B3167' : colors.navbarButton,
                            color: '#FFFFFF',
                            boxShadow: mode === 'dark' ? `inset 0 0 0 1px ${colors.border}` : undefined,
                            '&:hover': {
                                backgroundColor: mode === 'dark' ? '#1B273E' : colors.navigationHover,
                            },
                        },
                    },
                },
            },
        },
    });
};

export default createSpectreTheme;